import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, Store, Repeat, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { adminService } from '../../services/adminService';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Compute the set of weekday indices (0=Mon..6=Sun) allowed by the selected
// client's `preferences.allowed_delivery_days`. The recurring-schedule mask
// MUST be a subset of this set — the backend also validates, and the UI
// disables disallowed weekdays so the control is impossible to violate.
const indicesFromAllowedDays = (days) => {
  if (!Array.isArray(days) || days.length === 0) return new Set();
  const lower = days.map((d) => String(d || '').toLowerCase());
  return new Set(WEEKDAY_NAMES.map((n, i) => (lower.includes(n) ? i : null)).filter((v) => v !== null));
};

const emptyForm = {
  client_account_id: '',
  delivery_location_id: '',
  weekday_mask: [],
  items: [],
  start_date: '',
  end_date: '',
  no_action_policy: '',  // required — user must pick
  menu_change_policy: 'skip',
  po_number: '',
  notes: '',
};

// Derive the initial cadence mode from an incoming mask. A schedule with all
// 7 days selected renders as Daily; anything else (including empty) is Weekly.
const modeFromMask = (mask) => (Array.isArray(mask) && mask.length === 7 ? 'daily' : 'weekly');

// A single stateless dialog reused for BOTH admin create-on-behalf AND admin
// edit. In edit mode the client picker is locked, the delivery location dropdown
// is preloaded, and the dialog is titled + submits via PUT.
export const NewRecurringOrderDialog = ({
  open,
  onClose,
  onSaved,
  clients,
  editing,  // null = create; else the template object (must include id + client_account_id + fields)
}) => {
  const [form, setForm] = useState(emptyForm);
  const [cadenceMode, setCadenceMode] = useState('weekly');
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isEdit = !!editing;

  // Prime the dialog from the editing template or a fresh form on open.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const mask = [...(editing.weekday_mask || [])];
      setForm({
        client_account_id: editing.client_account_id,
        delivery_location_id: editing.delivery_location_id,
        weekday_mask: mask,
        items: (editing.items || []).map((i) => ({ ...i })),
        start_date: editing.start_date || '',
        end_date: editing.end_date || '',
        no_action_policy: editing.no_action_policy || '',
        menu_change_policy: editing.menu_change_policy || 'skip',
        po_number: editing.po_number || '',
        notes: editing.notes || '',
      });
      setCadenceMode(modeFromMask(mask));
    } else {
      setForm({ ...emptyForm });
      setCadenceMode('weekly');
      setLocations([]);
      setProducts([]);
      setPreview([]);
    }
  }, [open, editing]);

  // Refresh locations + priced catalog whenever the client selection changes.
  useEffect(() => {
    if (!form.client_account_id) {
      setLocations([]); setProducts([]); return;
    }
    const client = clients.find((c) => c.id === form.client_account_id);
    setLocations(client?.locations || []);
    (async () => {
      setProductsLoading(true);
      try {
        setProducts(await adminService.getProductsForOrder(form.client_account_id));
      } catch (e) {
        toast.error('Failed to load client catalog');
      } finally {
        setProductsLoading(false);
      }
    })();
  }, [form.client_account_id, clients]);

  // Live preview of the next 5 delivery dates whenever cadence/window changes.
  useEffect(() => {
    if (!form.weekday_mask.length || !form.start_date) { setPreview([]); return; }
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const dates = await adminService.previewRecurringDates({
          weekday_mask: form.weekday_mask,
          start_date: form.start_date,
          end_date: form.end_date || null,
          count: 5,
        });
        if (!cancelled) setPreview(dates);
      } catch {
        if (!cancelled) setPreview([]);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [form.weekday_mask, form.start_date, form.end_date]);

  const total = useMemo(
    () => form.items.reduce((s, i) => s + (i.unit_price || 0) * (i.quantity || 0), 0),
    [form.items],
  );

  // The selected client's allowed delivery days determine which weekdays this
  // schedule can run on. Falls back to an empty set when no client is picked
  // yet — in which case every chip is disabled and Daily is inert.
  const selectedClient = useMemo(
    () => clients.find((c) => c.id === form.client_account_id) || null,
    [clients, form.client_account_id],
  );
  const allowedIndices = useMemo(
    () => indicesFromAllowedDays(selectedClient?.allowed_delivery_days),
    [selectedClient],
  );
  const allowedLabels = useMemo(
    () => Array.from(allowedIndices).sort((a, b) => a - b).map((i) => WEEKDAY_LABELS[i]),
    [allowedIndices],
  );
  const clientHasAnyDay = allowedIndices.size > 0;
  const allSevenAllowed = allowedIndices.size === 7;

  // Keep the mask honest whenever the allowed-days envelope changes (e.g. when
  // the admin switches the client picker). Any previously selected weekday
  // that isn't in the new client's allowed days gets dropped automatically.
  useEffect(() => {
    if (!selectedClient || allowedIndices.size === 0) return;
    setForm((f) => {
      const filtered = f.weekday_mask.filter((d) => allowedIndices.has(d));
      if (filtered.length === f.weekday_mask.length) return f;
      return { ...f, weekday_mask: filtered };
    });
  }, [selectedClient, allowedIndices]);

  const toggleWeekday = (idx) => {
    if (!allowedIndices.has(idx)) return;  // disallowed day — chip is disabled
    setForm((f) => {
      const has = f.weekday_mask.includes(idx);
      const mask = has ? f.weekday_mask.filter((d) => d !== idx) : [...f.weekday_mask, idx].sort();
      return { ...f, weekday_mask: mask };
    });
  };

  const setDaily = () => {
    if (!clientHasAnyDay) return;
    setCadenceMode('daily');
    // "Daily" means "every allowed delivery day" for this client — so the mask
    // is the set of the client's allowed weekdays, not a hardcoded [0..6].
    setForm((f) => ({
      ...f,
      weekday_mask: Array.from(allowedIndices).sort((a, b) => a - b),
    }));
  };

  // Switching to Weekly always reveals the weekday chips. If we're coming FROM
  // Daily (all allowed days selected), clear the mask so the user can pick
  // specific days — otherwise keep their existing weekly selection intact.
  const setWeeklyMode = () => {
    setCadenceMode('weekly');
    setForm((f) => {
      const isAllAllowed = allowedIndices.size > 0
        && f.weekday_mask.length === allowedIndices.size
        && f.weekday_mask.every((d) => allowedIndices.has(d));
      return { ...f, weekday_mask: isAllAllowed ? [] : f.weekday_mask };
    });
  };

  const addItem = (product) => {
    if (form.items.find((i) => i.product_id === product.id)) return;
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.your_price,
        pack_size: product.pack_size,
        image_url: product.image_url,
      }],
    }));
  };

  const changeQty = (pid, q) => {
    const qty = Math.max(0, parseInt(q, 10) || 0);
    setForm((f) => ({
      ...f,
      items: f.items
        .map((i) => (i.product_id === pid ? { ...i, quantity: qty } : i))
        .filter((i) => i.quantity > 0),
    }));
  };

  const removeItem = (pid) => setForm((f) => ({
    ...f, items: f.items.filter((i) => i.product_id !== pid),
  }));

  const validate = () => {
    if (!form.client_account_id) { toast.error('Pick a client'); return false; }
    if (!form.delivery_location_id) { toast.error('Pick a delivery location'); return false; }
    if (!form.weekday_mask.length) { toast.error('Pick at least one delivery weekday'); return false; }
    if (!form.items.length) { toast.error('Add at least one item'); return false; }
    if (!form.start_date) { toast.error('Pick a start date'); return false; }
    if (!form.no_action_policy) { toast.error('Pick a no-action policy'); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        delivery_location_id: form.delivery_location_id,
        weekday_mask: form.weekday_mask,
        items: form.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        start_date: form.start_date,
        end_date: form.end_date || null,
        no_action_policy: form.no_action_policy,
        menu_change_policy: form.menu_change_policy,
        po_number: form.po_number || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        await adminService.updateRecurringTemplate(editing.id, payload);
        toast.success('Recurring schedule updated');
      } else {
        await adminService.createRecurringTemplate({
          client_account_id: form.client_account_id,
          ...payload,
        });
        toast.success('Recurring schedule created for client');
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to save recurring schedule';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const kind = cadenceMode;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="recurring-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat className="h-5 w-5 text-orange-600" />
            {isEdit ? 'Edit recurring schedule' : 'New recurring schedule — on behalf of client'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Edits affect future occurrence generation only — already-placed orders are not touched.'
              : 'This schedule will surface in the client’s portal with the same self-service tools they use for their own schedules. They’ll be notified when it’s created.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-2">
          {/* Client picker */}
          <div className="space-y-1">
            <Label>Client</Label>
            <Select
              value={form.client_account_id}
              onValueChange={(v) => setForm((f) => ({ ...f, client_account_id: v, delivery_location_id: '', items: [] }))}
              disabled={isEdit}
            >
              <SelectTrigger data-testid="recurring-client-select">
                <SelectValue placeholder="Choose a client" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`recurring-client-option-${c.id}`}>
                    {c.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location */}
          {form.client_account_id && (
            <div className="space-y-1">
              <Label>Delivery location</Label>
              <Select
                value={form.delivery_location_id}
                onValueChange={(v) => setForm((f) => ({ ...f, delivery_location_id: v }))}
              >
                <SelectTrigger data-testid="recurring-location-select">
                  <SelectValue placeholder="Choose a delivery location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`recurring-location-option-${l.id}`}>
                      {l.name || l.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Cadence */}
          <div className="space-y-2">
            <Label>Cadence</Label>
            {form.client_account_id && !clientHasAnyDay && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                data-testid="recurring-no-delivery-days-warning"
              >
                This client has no allowed delivery days configured. Add delivery days to their
                account before creating a recurring schedule.
              </div>
            )}
            {form.client_account_id && clientHasAnyDay && (
              <div
                className="text-xs text-gray-600"
                data-testid="recurring-allowed-days-hint"
              >
                This client accepts deliveries on: <span className="font-medium">
                  {allSevenAllowed ? 'every day of the week' : allowedLabels.join(', ')}
                </span>. Other days are disabled.
              </div>
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={kind === 'daily' ? 'default' : 'outline'}
                onClick={setDaily}
                disabled={!clientHasAnyDay || !allSevenAllowed}
                title={!allSevenAllowed && clientHasAnyDay
                  ? 'Client accepts fewer than 7 delivery days — use Weekly to pick allowed days.'
                  : undefined}
                data-testid="recurring-cadence-daily"
              >
                Daily
              </Button>
              <Button
                type="button"
                variant={kind === 'weekly' ? 'default' : 'outline'}
                onClick={setWeeklyMode}
                disabled={!clientHasAnyDay}
                data-testid="recurring-cadence-weekly"
              >
                Weekly
              </Button>
            </div>
            {kind === 'weekly' && (
              <div className="flex flex-wrap gap-2 pt-1">
                {WEEKDAY_LABELS.map((lbl, idx) => {
                  const isAllowed = allowedIndices.has(idx);
                  const isSelected = form.weekday_mask.includes(idx);
                  return (
                    <Button
                      key={idx}
                      type="button"
                      size="sm"
                      variant={isSelected ? 'default' : 'outline'}
                      onClick={() => toggleWeekday(idx)}
                      disabled={!isAllowed}
                      title={isAllowed ? undefined : `${lbl} is not an allowed delivery day for this client`}
                      className={!isAllowed ? 'opacity-40 line-through cursor-not-allowed' : undefined}
                      data-testid={`recurring-weekday-${idx}`}
                    >
                      {lbl}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Schedule window */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Start date</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                data-testid="recurring-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label>End date (optional)</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                data-testid="recurring-end-date"
              />
            </div>
          </div>

          {/* Live preview */}
          {(previewLoading || preview.length > 0) && (
            <div className="rounded-md border border-orange-200 bg-orange-50/60 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-orange-900 mb-2">
                <CalendarDays className="h-4 w-4" />
                Next 5 delivery dates
              </div>
              {previewLoading ? (
                <div className="text-xs text-orange-700">Loading…</div>
              ) : (
                <ul className="text-sm text-orange-900 space-y-0.5">
                  {preview.map((d) => (
                    <li key={d.delivery_date} data-testid={`recurring-preview-${d.delivery_date}`}>
                      <span className="font-medium">{d.weekday}</span> · {d.delivery_date}
                      {' '}<span className="text-xs text-orange-700">(deadline morning of {d.cutoff_date})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Items */}
          {form.client_account_id && (
            <div className="space-y-2">
              <Label>Items</Label>
              {productsLoading ? (
                <div className="text-sm text-gray-500">Loading client catalog…</div>
              ) : (
                <>
                  <Select onValueChange={(pid) => {
                    const p = products.find((x) => x.id === pid);
                    if (p) addItem(p);
                  }} value="">
                    <SelectTrigger data-testid="recurring-product-select">
                      <SelectValue placeholder="Add a product…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`recurring-product-option-${p.id}`}>
                          {p.name} — ${p.your_price?.toFixed?.(2) ?? p.your_price} / {p.pack_size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="space-y-2">
                    {form.items.length === 0 && (
                      <div className="text-sm text-gray-500 italic">No items yet.</div>
                    )}
                    {form.items.map((i) => (
                      <div key={i.product_id} className="flex items-center gap-3 rounded-md border p-2"
                        data-testid={`recurring-item-${i.product_id}`}>
                        <div className="flex-1">
                          <div className="text-sm font-medium">{i.product_name}</div>
                          <div className="text-xs text-gray-500">
                            ${(i.unit_price || 0).toFixed(2)} / {i.pack_size}
                          </div>
                        </div>
                        <Input
                          type="number"
                          min={1}
                          value={i.quantity}
                          onChange={(e) => changeQty(i.product_id, e.target.value)}
                          className="w-20"
                          data-testid={`recurring-item-qty-${i.product_id}`}
                        />
                        <div className="w-24 text-right text-sm font-medium">
                          ${((i.unit_price || 0) * i.quantity).toFixed(2)}
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeItem(i.product_id)}
                          data-testid={`recurring-item-remove-${i.product_id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end text-sm font-medium" data-testid="recurring-order-total">
                    Order total: <span className="ml-1">${total.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Policies */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>If the client takes no action by the deadline</Label>
              <Select
                value={form.no_action_policy}
                onValueChange={(v) => setForm((f) => ({ ...f, no_action_policy: v }))}
              >
                <SelectTrigger data-testid="recurring-no-action-select">
                  <SelectValue placeholder="Choose a policy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto_place" data-testid="recurring-no-action-auto-place">
                    Place automatically
                  </SelectItem>
                  <SelectItem value="auto_skip" data-testid="recurring-no-action-auto-skip">
                    Skip if no action
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>If an item isn’t on that day’s menu</Label>
              <Select
                value={form.menu_change_policy}
                onValueChange={(v) => setForm((f) => ({ ...f, menu_change_policy: v }))}
              >
                <SelectTrigger data-testid="recurring-menu-change-select">
                  <SelectValue placeholder="Choose a policy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip" data-testid="recurring-menu-change-skip">
                    Skip the whole order
                  </SelectItem>
                  <SelectItem value="place_available" data-testid="recurring-menu-change-place-available">
                    Place what’s available
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* PO + notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>PO number (optional)</Label>
              <Input
                value={form.po_number}
                onChange={(e) => setForm((f) => ({ ...f, po_number: e.target.value }))}
                placeholder="Client PO reference"
                data-testid="recurring-po-number"
              />
            </div>
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Any standing instructions for these deliveries"
                data-testid="recurring-notes"
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="recurring-cancel-button">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="recurring-submit-button">
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create schedule')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewRecurringOrderDialog;
